import React, { useEffect } from 'react';
import { observer } from 'mobx-react';

import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { UiModes, BlockSize } from 'libs/rendering/gltf/config';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';
import { routerStore } from 'core/routing';
import { FloatingTabs, FloatingTab } from 'components';

import { getInstancePublicName } from 'libs/rendering/gltf/components/tree/treeNode';
import { FloatingTabsID } from 'constants/cad';
import { useGetElementInfo } from 'hooks';

import { Navigation, NavigationRight, IconStyled } from './tab.styled';

const TabManager = observer(function _TabManager() {
  const nodeRef = React.createRef();
  const viewerControl = useViewerControl();

  const { height: floatTabHeight = 0 } = useGetElementInfo(
    `#${FloatingTabsID}`,
  );

  const { settings } = viewerControl.viewer || {};

  const currCamera = settings?.CurrentCamera();
  const cameraCount = settings.CameraCount();
  const tabLoadingStatus = currCamera?.GetLoadStatus();

  useEffect(() => {
    // tab change, update UI mode
    if (currCamera) {
      if (currCamera.IsDrawing()) {
        return;
      }

      const isSection = viewerControl.uiMode === UiModes.section;
      if (!isSection) {
        const prevMode = viewerControl.viewer?.mode;
        const newMode = currCamera.GetDefaultUiMode(prevMode);

        // sync wasm and viewerControl ui mode...
        if (viewerControl.uiMode !== newMode?.value) {
          if (viewerControl.uiMode !== UiModes.collapsed) {
            viewerControl.UpdateUiMode(newMode?.value);
          }
        }

        const selModeEnum = viewerControl.viewer?.module.SelectionMode;
        const selMode =
          viewerControl.viewer?.selectionManager?.GetSelectionMode();
        const keepSelMode = selMode === selModeEnum.SelectionModeMeasurements;
        if (keepSelMode) return;

        // sync selection state only...
        viewerControl.SyncSelectionMode();
      }
    }
  }, [viewerControl.getLeftOffset(), currCamera]);

  if (!currCamera || tabLoadingStatus !== 0) return null;
  if (cameraCount === 1 && !settings.GetCamera(0).IsAssembly()) return null;

  const scrollTab = ix => {
    if (ix > 0) {
      nodeRef.current.scrollLeft += BlockSize.constWidth;
    } else if (ix < 0) {
      nodeRef.current.scrollLeft -= BlockSize.constWidth;
    }
  };

  const shouldShowScroll = () => {
    return nodeRef.current?.scrollWidth > nodeRef.current?.clientWidth;
  };

  function updateTab(cameraIx, force = false) {
    if (
      Number.isInteger(cameraIx) &&
      cameraIx >= 0 &&
      (force || cameraIx !== settings.CurrentCamera()?.GetIndex())
    ) {
      const newParams = routerStore.route.params;
      if (newParams.panel === 'rfq') {
        delete newParams.panel; // close rfq when switching tab
      }

      // update URL
      // - see InspectCadItem in viewerControl.js for some additional details,
      // this function is basicly a duplicate of the navigate code in there
      const instance = settings.GetCamera(cameraIx)?.GetInstance();
      const product =
        !instance && cameraIx > 0
          ? settings.GetCamera(cameraIx)?.GetSubAssembly()
          : null;

      viewerControl.InspectCadItem(instance || product);
    }
  }

  function closeTab(cameraIx) {
    if (Number.isInteger(cameraIx) && cameraIx >= 0) {
      const currCameraIx = currCamera?.GetIndex();
      const closingCurrent = currCameraIx === cameraIx;
      const closingItem = currCamera?.GetItem();

      // delete
      settings.DeleteCamera(cameraIx);
      if (closingCurrent) {
        // switch to default (1st) camera
        updateTab(0, true);
      }

      // update inspected flag changed in wasm when deleting the tab
      if (closingItem) {
        closingItem.reportWasmChange();
      }
    }
  }

  // tabs
  function tabsArrayBuilder() {
    const tabs = [];
    if (cameraCount <= 1) return null;

    for (let i = 0; i < cameraCount; i++) {
      const camera = settings.GetCamera(i);
      const isClosable = camera?.GetIndex() > 0;
      let label = camera?.label;
      if (camera?.IsInstance()) {
        label = getInstancePublicName(camera?.GetInstance());
      }

      const icon =
        camera?.GetIndex() > 0 ? (
          camera.IsAssembly() ? (
            <IconStyled name='assembly' size='small' />
          ) : camera.IsSubAssembly() ? (
            <IconStyled name='sub_assembly' size='small' isSubassembly />
          ) : camera.IsDrawing() ? (
            <IconStyled
              name='drawing'
              size='small'
              viewBox='0 0 40 40'
              isDrawing
            />
          ) : camera.IsInstance() &&
            camera.GetInstance().GetBody().GetBrep() ? (
            <IconStyled name='brep' size='small' viewBox='0 0 40 40' isBody />
          ) : (
            <IconStyled name='mesh' size='small' viewBox='0 0 40 40' isBody />
          )
        ) : (
          <IconStyled name='assembly' size='small' />
        );

      tabs.push(
        <FloatingTab
          key={camera?.GetIndex()}
          closable={isClosable}
          value={camera?.GetIndex()}
        >
          {icon}
          {label}
        </FloatingTab>,
      );
    }

    const alreadyWrapped = floatTabHeight > 40;

    return (
      <FloatingTabs
        tabStyle={{ rowGap: 4 }}
        id={FloatingTabsID}
        className={`flex-wrap mt-2 ${alreadyWrapped ? 'mt-4 ml-2' : ''}`}
        value={currCamera?.GetIndex()}
        onClick={(event, value) => {
          updateTab(value);
        }}
        onClose={value => {
          closeTab(value);
        }}
      >
        {tabs}
      </FloatingTabs>
    );
  }

  return (
    <>
      {
        <>
          <Tooltip delayShow={500} place='right' />
          <Navigation
            // paddingLeft={
            //   isMobile() || !clientStore.signedInProfile
            //     ? 0
            //     : viewerControl.getLeftOffset()
            // }
            shouldScroll={shouldShowScroll()}
          >
            {tabsArrayBuilder()}
          </Navigation>
          {shouldShowScroll() && (
            <NavigationRight>
              <ul>
                <li>
                  <Icon
                    name='arrow_left'
                    size='default'
                    onClick={() => scrollTab(-1)}
                  />
                </li>
                <li>
                  <Icon
                    name='arrow_right'
                    size='default'
                    onClick={() => scrollTab(1)}
                  />
                </li>
              </ul>
            </NavigationRight>
          )}
        </>
      }
    </>
  );
});

export default TabManager;
